/**
 * Theme Presets
 *
 * Each theme overrides only accent tokens (primary, ring, gradient).
 * Structural tokens (background, border, muted, foreground) are handled
 * by @speakai/ui and stay consistent across all themes.
 *
 * To add a new theme: add an entry here. The ThemeApplier component
 * will automatically apply it by setting CSS variables on <html>.
 */
import { ACCENT_PRESETS, DEFAULT_ACCENT_ID, type AccentPreset, type ModeValues } from "@speakai/shared";

/** Speak's brand orange accent, for surfaces that need a literal hex value (e.g. widget defaults). */
export const BRAND_ORANGE = "#f97316";

export interface AppTheme {
  id: string;
  name: string;
  /** Preview colors shown in the theme picker card */
  preview: {
    primary: string;
    secondary: string;
  };
  tokens: {
    light: Record<string, string>;
    dark: Record<string, string>;
  };
}

interface Gradient {
  from: string;
  to: string;
  accent: string;
}

const GRADIENTS: Readonly<Record<string, ModeValues<Gradient>>> = {
  default: {
    light: { from: "hsl(0 0% 9%)", to: "hsl(0 0% 35%)", accent: "hsl(0 0% 25%)" },
    dark: { from: "hsl(0 0% 95%)", to: "hsl(0 0% 60%)", accent: "hsl(0 0% 50%)" },
  },
  indigo: {
    light: { from: "hsl(239 84% 67%)", to: "hsl(239 84% 57%)", accent: "hsl(239 84% 47%)" },
    dark: { from: "hsl(239 84% 67%)", to: "hsl(239 84% 77%)", accent: "hsl(239 84% 57%)" },
  },
  emerald: {
    light: { from: "hsl(160 84% 39%)", to: "hsl(160 84% 29%)", accent: "hsl(160 84% 49%)" },
    dark: { from: "hsl(160 84% 45%)", to: "hsl(160 84% 55%)", accent: "hsl(160 84% 35%)" },
  },
  rose: {
    light: { from: "hsl(350 89% 60%)", to: "hsl(350 89% 50%)", accent: "hsl(350 89% 70%)" },
    dark: { from: "hsl(350 89% 60%)", to: "hsl(350 89% 70%)", accent: "hsl(350 89% 50%)" },
  },
  orange: {
    light: { from: "hsl(25 95% 53%)", to: "hsl(25 95% 43%)", accent: "hsl(25 95% 63%)" },
    dark: { from: "hsl(25 95% 53%)", to: "hsl(25 95% 63%)", accent: "hsl(25 95% 43%)" },
  },
  purple: {
    light: { from: "hsl(263 70% 50%)", to: "hsl(330 81% 60%)", accent: "hsl(24 95% 53%)" },
    dark: { from: "hsl(263 70% 60%)", to: "hsl(330 81% 65%)", accent: "hsl(24 95% 63%)" },
  },
};

function themeTokens(colors: AccentPreset["light"], gradient: Gradient): Record<string, string> {
  return {
    "--color-primary": colors.accent,
    "--color-primary-foreground": colors.onAccent,
    "--color-ring": colors.accent,
    "--color-gradient-from": gradient.from,
    "--color-gradient-to": gradient.to,
    "--color-gradient-accent": gradient.accent,
  };
}

export const APP_THEMES: AppTheme[] = ACCENT_PRESETS.map((preset) => {
  const gradient = GRADIENTS[preset.id] ?? GRADIENTS[DEFAULT_ACCENT_ID];
  return {
    id: preset.id,
    name: preset.name,
    preview: preset.preview,
    tokens: { light: themeTokens(preset.light, gradient.light), dark: themeTokens(preset.dark, gradient.dark) },
  };
});

export function getThemeById(id: string): AppTheme {
  return APP_THEMES.find((t) => t.id === id) || APP_THEMES[0];
}

/**
 * Available font options.
 * Fonts are loaded dynamically from Google Fonts only when selected.
 * "system" uses the browser's default system font stack.
 * "dm-sans" is pre-loaded via next/font (no extra network request).
 */
export const APP_FONTS = [
  { id: "system", name: "System Default", family: "" },
  { id: "dm-sans", name: "DM Sans", family: "DM Sans" },
  { id: "inter", name: "Inter", family: "Inter" },
  { id: "outfit", name: "Outfit", family: "Outfit" },
  {
    id: "plus-jakarta",
    name: "Plus Jakarta Sans",
    family: "Plus Jakarta Sans",
  },
  { id: "geist", name: "Geist Sans", family: "Geist" },
  { id: "poppins", name: "Poppins", family: "Poppins" },
  { id: "nunito", name: "Nunito Sans", family: "Nunito Sans" },
  { id: "lato", name: "Lato", family: "Lato" },
  { id: "open-sans", name: "Open Sans", family: "Open Sans" },
  { id: "raleway", name: "Raleway", family: "Raleway" },
  { id: "manrope", name: "Manrope", family: "Manrope" },
  { id: "space-grotesk", name: "Space Grotesk", family: "Space Grotesk" },
  { id: "sora", name: "Sora", family: "Sora" },
  { id: "figtree", name: "Figtree", family: "Figtree" },
] as const;

export type AppFontId = (typeof APP_FONTS)[number]["id"];
export const DEFAULT_FONT_ID: AppFontId = "dm-sans";
